<script lang="ts">
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import DonutChart from "@shared/svelte/Components/DonutChart.svelte";
  import ProductExpander from "@modern/components/retailer/reports/ProductExpander.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { OnLayoutChange } from "@shared/svelte/Directives/OnLayoutChange";
  import type { DistributorOrderDetail } from "@shared/types/Reports";

  export let reportsOrdersDetail: DistributorOrderDetail[];
  export let startDate;
  export let endDate;

  const donutChartOptionOverrides = {
    legend: {
      display: true,
      position: "right",
      fullWidth: true,
      labels: {
        boxWidth: 14,
        fontSize: 14,
        fontFamily:
          "'Lato', '-apple-system', 'BlinkMacSystemFont', 'San Francisco', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'sans-serif'",
      },
    },
    title: { display: false },
    cutoutPercentage: 80,
    responsive: true,
    maintainAspectRatio: false,
  };

  let categoriesData = {};
  let productsDetailData = {};
  let monthsDetailData = {};

  $: {
    reportsOrdersDetail.forEach(function (d) {
      HttpPromise({
        url: "/api/retailer/reports/overview_categories",
        method: "GET",
        data: {
          start_date: startDate,
          end_date: endDate,
          distributor_id: d.distributor_id,
        },
      }).then(function (res) {
        categoriesData[d.distributor_id] = res;
      });
    });
  }

  const getDetail = distributorId => {
    if (!productsDetailData[distributorId]) {
      monthsDetailData[distributorId] = {};
      HttpPromise({
        url: "/api/retailer/reports/orders_detail_products",
        method: "GET",
        data: {
          start_date: startDate,
          end_date: endDate,
          distributor_id: distributorId,
        },
      }).then(function (res) {
        productsDetailData[distributorId] = res;
        productsDetailData[distributorId]["showDetail"] = true;
      });
      return;
    }
  };

  const toggleDetail = distributorId => {
    if (!productsDetailData[distributorId]) {
      getDetail(distributorId);
      return;
    }
    productsDetailData[distributorId]["showDetail"] =
      !productsDetailData[distributorId]["showDetail"];
  };

  let layoutName = "desktop";
  const updateLayoutName = name => (layoutName = name);

</script>

{#if !reportsOrdersDetail.length}
  <div class="grid-container margin-bottom--medium">
    <div class="grid-row margin-top--small">
      <div class="grid-col c12-12">
        <div class="background-white padding--smaller report-dashboard__container">
          <div class="s3 align-center">No data found for the dates selected.</div>
        </div>
      </div>
    </div>
  </div>
{/if}

<div use:OnLayoutChange={updateLayoutName} class="grid-container margin-top--small">
  {#each reportsOrdersDetail as distributorOrderDetail}
    <div
      class="grid-row margin-bottom--small background-white"
      id="report_distributor_{distributorOrderDetail.distributor_id}"
    >
      <div class="grid-col c12-12">
        <div class="padding--smaller">
          <div class="grid-row">
            <div
              class="grid-col c12-12 c7-12--md c8-12--lg report-order__header padding-right--small--md"
            >
              <div class="grid-row padding-bottom--small--md padding-top--smaller">
                <div class="grid-col c7-12 align-left">
                  <span class="default-subheader--bold display-block notranslate"
                    >{distributorOrderDetail.distributor_name}</span
                  >
                </div>
                <div class="grid-col c5-12 align-right">
                  <!-- svelte-ignore a11y-missing-attribute -->
                  <a
                    class="brand-body--bold cursor-pointer details-button"
                    on:click={() => toggleDetail(distributorOrderDetail.distributor_id)}
                  >
                    {productsDetailData[distributorOrderDetail.distributor_id] &&
                    productsDetailData[distributorOrderDetail.distributor_id]["showDetail"]
                      ? "Hide Details -"
                      : "Show Details +"}
                  </a>
                </div>
              </div>
              <div class="report-order__header--separator" />
              <hr class="hr hide--md margin-bottom--smaller margin-top--smaller" />
              <div class="grid-row margin-top--small--md">
                <div class="grid-col c4-12 vertical-align-bottom">
                  <div class="secondary-small--regular uppercase ellipsis">Orders</div>
                </div>
                <div class="grid-col c4-12 vertical-align-bottom">
                  <div class="secondary-small--regular uppercase ellipsis">Products</div>
                </div>
                <div class="grid-col c4-12 vertical-align-bottom">
                  <div class="secondary-small--regular uppercase ellipsis">Spend</div>
                </div>
              </div>
              <div class="grid-row margin-top--smaller margin-bottom--smaller">
                <div class="grid-col c4-12 vertical-align-bottom">
                  <div
                    class="default-body--bold default-subheader--bold--md distributor-report-num-orders"
                  >
                    {distributorOrderDetail.num_orders}
                  </div>
                </div>
                <div class="grid-col c4-12 vertical-align-bottom">
                  <div
                    class="default-body--bold default-subheader--bold--md distributor-report-num-products"
                  >
                    {distributorOrderDetail.num_products}
                  </div>
                </div>
                <div class="grid-col c4-12 vertical-align-bottom">
                  <div
                    class="default-body--bold default-subheader--bold--md color-green distributor-report-spend"
                  >
                    <FormatMoney input={distributorOrderDetail.spend} />
                  </div>
                </div>
              </div>
            </div>
            <div
              class="grid-col c12-12 c5-12--md c4-12--lg position-relative report-order__categories"
            >
              <div>
                {#if categoriesData[distributorOrderDetail.distributor_id]}
                  <DonutChart
                    chartOptionOverrides={donutChartOptionOverrides}
                    extraClasses={"reports-overview-categories"}
                    {categoriesData}
                    distributorReport={distributorOrderDetail}
                  />
                {/if}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ProductExpander
        showDetails={productsDetailData[distributorOrderDetail.distributor_id]}
        productsDetailData={productsDetailData[distributorOrderDetail.distributor_id]}
        distributorId={distributorOrderDetail.distributor_id}
        {monthsDetailData}
        {layoutName}
      />
    </div>
  {/each}
</div>
